/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useTranslation } from 'react-i18next';

import { Namespaces } from '../../../../i18n';
import { SUUser } from '../../../../types/superuser/user/SUUser';
import { useDetailPanelStyles } from './UsersDetailPanel.styles';

type UsersDetailPanelProps = {
  rowData: SUUser;
};

export function UsersDetailPanel(props: UsersDetailPanelProps) {
  const classes = useDetailPanelStyles();
  const { t } = useTranslation(Namespaces.Admin);
  const { rowData } = props;

  return (
    <div className={classes.detailPanelRoot}>
      <div className={classes.blockInfo}>
        <b>{t('users.table.labels.name')}</b>:
        <span>{`${rowData.firstName} ${rowData.middleName || ''} ${rowData.lastName}`}</span>
      </div>
      <div className={classes.blockInfo}>
        <b>{t('users.table.labels.superuser')}</b>:
        <span>{rowData.isSuperuser ? t('users.description_panel.yes') : t('users.description_panel.no')}</span>
      </div>
      <div className={classes.blockInfo}>
        <b>{t('users.table.labels.activated')}</b>:
        <span>{!rowData.isDisabled ? t('users.description_panel.yes') : t('users.description_panel.no')}</span>
      </div>
      <div className={classes.blockInfo}>
        <b>{t('users.table.labels.verified')}</b>:
        <span>{rowData.verified ? t('users.description_panel.yes') : t('users.description_panel.no')}</span>
      </div>
      {rowData.address && (
        <>
          {
            <div className={classes.blockInfo}>
              <b>{t('users.description_panel.labels.country')}</b>:
              <span>{`${t(`countries:${rowData.address.countryCode}.name`)}, ${rowData.address.countryCode}`}</span>
            </div>
          }
          {rowData.address.city && (
            <div className={classes.blockInfo}>
              <b>{t('users.description_panel.labels.city')}</b>:<span>{rowData.address?.city}</span>
            </div>
          )}
          {rowData.address.state && (
            <div className={classes.blockInfo}>
              <b>{t('users.description_panel.labels.state')}</b>:<span>{rowData.address?.state}</span>
            </div>
          )}
          <div className={classes.blockInfo}>
            <b>{t('users.description_panel.labels.address')}</b>:
            <span>
              {`${rowData.address?.line1 || '—'}, 
              ${rowData.address?.line2 || '—'}, 
              ${rowData.address?.line3 || '—'}`}
            </span>
          </div>
          {rowData.address.postalCode && (
            <div className={classes.blockInfo}>
              <b>{t('users.description_panel.labels.postal_code')}</b>:<span>{rowData.address?.postalCode}</span>
            </div>
          )}
        </>
      )}
      {rowData.phoneNationalNumber && rowData.phoneCountryCode && (
        <div className={classes.blockInfo}>
          <b>{t('users.description_panel.labels.phone')}</b>:
          <span>{`+(${t(`countries:${rowData.phoneCountryCode}.callingCode`)}) ${rowData.phoneNationalNumber}, ${t(
            `countries:${rowData.phoneCountryCode}.name`,
          )}`}</span>
        </div>
      )}
      <div className={classes.blockInfo}>
        <b>{t('users.description_panel.labels.email')}</b>:<span>{rowData.email}</span>
      </div>
    </div>
  );
}
